import React, { useMemo, useCallback, useState, useRef, useEffect, useContext, memo } from 'react'
import { createEditor, Transforms, Editor, Node, Text, Operation } from 'slate'
import { Slate, Editable, withReact, ReactEditor } from 'slate-react'
import { withHistory } from 'slate-history'
import { cx } from '@emotion/css'
import { cloneDeep, isEqual, pick } from 'lodash'
import { v4 } from 'uuid'
import siteViewStyles from '../../styles.module.css'
import { savedSelection } from '../../utils/savedSelection'
//@ts-ignore
import styles from './style.module.css'
import { Icon } from '../../../../../index'
import { Element, Leaf } from '../../elements'
import { MainToolbar, HoveringToolbar } from '../Toolbar'
import { applyPlugins, useRtePlugins, dndResetProps, useCustomToolbar } from '../../utils'
import { EditorProvider, ScrollRefProvider, ReduxProvider } from '../ContextProvider'
import { handleShortcut } from '../../utils/shortcuts/handleShortcut'
import { handleRteOverlayClick, useEditorResize, useRteFullScreen, handleModalZIndexOnFullscreen } from '../../../../../utils/hooks'
import dndStyles from '../Dnd/style.module.css'

import { useExtensionFullScreen } from '../../utils/useExtensionFullScreen'
import { SlashElement, SlashComponent } from '../SlashCommand'
import { useDecorate } from '../../utils/hooks/useDecorate'
import { EditorElementProps } from './types'
import DiscussionPopup from '../Discussion/DiscussionPopup'
import { useDiscussion } from '../Discussion/utils/useDiscussion'
import { failureNotification, fetchActiveDiscussions } from '../Discussion/utils/request'
import { HoveringToolbarProvider } from '../ContextProvider/HoveringToolbarProvider'
import { IHoveringToolbarContext } from '../../utils/types'
import { getDiscussionMarksOnTextNode, isDiscussionThreadActive } from '../../elements/Leaf/rangeComment/helpers'
import isHotkey from 'is-hotkey'
import { useDidUpdate } from '../../../../../utils/hooks/componentLifecycle'
import { ResizeIcon } from '../../elements/utils/Icon'
import useEntrySave from '../../utils/hooks/useEntrySave'
import { TriggerButton } from '../Toolbar/Button/TriggerButton'
import { Registry } from '..'
import useDiscussionListeners from '../Discussion/utils/hooks/useDiscussionListeners'
import useRTESizeListener from './useRteSizeListener'
import useRTEEmbedListeners from './hooks/useRTEEmbedListeners'
import useShowProperties from './hooks/useShowProperties'
import useScrollToBlockListener from './hooks/useScrollToBlockListener'

const FullScreenToggle = memo(({editorRef, editor, isFullScreen, isEntryInModal }: { editorRef: React.RefObject<HTMLDivElement>, editor: any, isFullScreen: boolean, isEntryInModal?: boolean }) => {
  const fullscreen: Record<string, any> = Registry.get({
    id: 'fullscreen',
    registry: 'v2.element',
    conditions: {
      overide: true
    }
  })

  const Icon = fullscreen?.iconName;
  const ActiveIcon = fullscreen?.activeIcon;

  return (
    <a className={styles['scrte-fullscreen-row-button']} onClick={(e) => {
      e.preventDefault();
      const postRobot = window.postRobot;
      let classes = editorRef.current.classList;
      const { isPlugin } = editor.requestProps || {};
      if (isPlugin) {
        postRobot.sendToParent('goFullscreen', { open: !classes.contains('fullscreen'), isEntryInModal });
      }
      classes.toggle('fullscreen');
      
      // Handle modal z-index when in modal context
      if (isEntryInModal) {
        handleModalZIndexOnFullscreen(editorRef);
      }
    }} >
      {isFullScreen ? ActiveIcon : Icon}
      <span className={styles['scrte-fullscreen-row-text']} >{isFullScreen ? "Exit Full Screen" : "Expand Text Editor"}</span>
    </a>
  )
});


const EditorElement = (props: EditorElementProps) => {

  const {
    customToolbarComponents,
    value = [
      {
        type: 'doc',
        children: [
          {
            type: 'h1',
            attrs: {},
            children: [{ text: 'Hello, world!' }]
          }
        ]
      }
    ],
    onChange = () => { },
    toolbar = true,
    hoveringToolbar = true,
    contentStack,
    toolbarMode,
    allowExtraTags = {},
    contentTypeNameMap = {},
    disabled = false,
    fieldName = 'Supercharged RTE',
    customPasteOptions = {},
    copyPasteConfig,
    csOnlyBreakline,
    customLocaleRTL,
    csBreakLineOnEnter,
    rteVersion,
    comments = {
      enabled: false
    },
    fieldMetadata,
    entryMetadata,
    stackMetadata,
    referenceActions,
    allLocales,
    useV2EmbedPicker,
    imageSizeInputEnabled,
    customChooseFromAssetsModal,
    customEmbedAssetsModal,
    openAMV2UploadTarget,
    requestProps = {},
    onFocus = () => {
      // It is intentionally left blank
    },
    onBlur = () => {
      // It is intentionally left blank
    },
    onMouseEnter = () => {
      // It is intentionally left blank
    },
    onMouseLeave = () => {
      // It is intentionally left blank
    },
    // placeholder = `Type '/' for options`,
    // uid = null,
    required = false,
    // contentTypeNameMap: { },
  } = props;

  const scrollRef = useRef(null)
  const editorRef = useRef(null)
  const singleLineRef = useRef(null)
  const editorResizeRef = useRef(null)
  const toolbarSizeRef = useRef(null)
  const [slateValue, setSlateValue] = useState(value)
  const [plugins, setPlugins] = useState([])
  const [triggerRerenderingAfterDnd, setTriggerRerenderingAfterDnd] = useState(Date.now())

  const editor = useMemo(() => applyPlugins(withHistory(withReact(createEditor() as ReactEditor))), [])
  editor['toolbarMode'] = toolbarMode
  editor.useV2EmbedPicker = useV2EmbedPicker
  editor.imageSizeInputEnabled = imageSizeInputEnabled;
  editor.isDraft = props.isDraft
  editor.customChooseFromAssetsModal = customChooseFromAssetsModal
  editor.customEmbedAssetsModal = customEmbedAssetsModal
  const customToolbarComponentsCopy = useCustomToolbar(customToolbarComponents, editor)

  const { activeDiscussion, setActiveDiscussion, discussions, setDiscussions, removeDiscussion } = useDiscussion()

  const isFullScreen = useRteFullScreen(editorRef)

  const [isFetchingDiscussions, setIsFetchingDiscussion] = useState(true)

  useEntrySave(editor, props?.requestProps?.isPlugin)
  useShowProperties({ editor, isPlugin: props?.requestProps?.isPlugin, key: 'json-rte-show-properties' })
  useRTEEmbedListeners({
    editor,
    editorRef,
    isPlugin: props?.requestProps?.isPlugin,
    fieldMetadata,
    entryMetadata,
    isEntryInModal: props.isEntryInModal,
    isEntryInModalEnabled: props.isEntryInModalEnabled,
    isMainAppCreateEntryRoute: props.isMainAppCreateEntryRoute,
  })
  useDiscussionListeners({ path: fieldMetadata?._metadata?.path, setActiveDiscussion, discussions, isFetchingDiscussions, isPlugin: props?.requestProps?.isPlugin })
  useRTESizeListener({ toolbarSizeRef, path: fieldMetadata?._metadata?.path })
  useScrollToBlockListener({ absolutePath: fieldMetadata?.['absolutePath'], isPlugin: props?.requestProps?.isPlugin })
  useDidUpdate(() => {
    if (!isEqual(slateValue, value)) {
      Transforms.removeNodes(editor, { at: [0] })
      Transforms.insertNodes(editor, value, { at: [0] })
      if (editor.isEntrySaving) {
        delete editor['mockOperations']
        editor.isEntrySaving = false
      }
    }
  }, [props.updateValueFromAppSdk])

  useEffect(() => {
    if (!isEqual(slateValue, value)) {
      editor.selection = null
      setSlateValue(value)
      editor.children = value
    }
  }, [value])

  // Removing resolved stale discussion marks, for discussion

  useEffect(() => {
    if (isFetchingDiscussions) return

    const staleDiscussionThreads = new Set<string>()
    const textNodes = Array.from(Editor.nodes(editor, { at: [0], mode: 'lowest', match: Text.isText }))
    textNodes.forEach(([node]) => {
      const threads = getDiscussionMarksOnTextNode(node)
      threads.forEach((thread) => {
        if (!isDiscussionThreadActive(thread, discussions)) {
          staleDiscussionThreads.add(thread)
        }
      })
    })
    // Discussions can finish loading while the user is editing, so leave the selection alone when
    // nothing is stale; a range ref follows the caret through the leaf merges removeMark causes.
    if (staleDiscussionThreads.size === 0) return
    const selectionRef = editor.selection ? Editor.rangeRef(editor, editor.selection) : null
    try {
      Transforms.select(editor, [0])
      staleDiscussionThreads.forEach((thread) => {
        Editor.removeMark(editor, thread)
      })
      const restoredSelection = selectionRef?.unref()
      if (restoredSelection) {
        Transforms.select(editor, restoredSelection)
      } else {
        Transforms.deselect(editor)
      }
      editor.history?.undos?.pop()
    } catch (e) {
      selectionRef?.unref()
      console.error(e)
    }
  }, [isFetchingDiscussions])

  useEffect(() => {
    editor.requestProps = requestProps
  }, [requestProps])

  useEffect(() => {
    editor.openAMV2UploadTarget = openAMV2UploadTarget
  }, [openAMV2UploadTarget])

  const handleKeyDown = (event) => {
    handleShortcut(event, editor, {
      customToolbarComponents: customToolbarComponentsCopy,
      contentStack,
      toolbarMode
    })
    handleKeyDownForSlash(event)
  }

  const elementRenderer = useCallback(
    (props) => (
      <Element {...props} contentTypeNameMap={contentTypeNameMap} setTriggerRerenderingAfterDnd={setTriggerRerenderingAfterDnd} singleLineRef={singleLineRef} discussion={{ comments, setActiveDiscussion, discussions }} />
    ),
    [plugins.length, discussions, triggerRerenderingAfterDnd, comments?.enabled]
  )
  const leafRenderer = useCallback(
    (props) => (
      <Leaf
        {...props}
        contentTypeNameMap={contentTypeNameMap}
        discussionUtils={{ enabled: comments?.enabled, setActiveDiscussion, discussions, activeDiscussion }}
      />
    ),
    [plugins.length, discussions, activeDiscussion, comments?.enabled, isFetchingDiscussions]
  )


  let newProps = {
    editorRef: editorRef
  }
  const fieldMetadataKeys = [
    'data_type',
    'display_name',
    'uid',
    'field_metadata',
    'reference_to',
    'multiple',
    '_metadata',
  ] as const
  const discussionProps = {
    entryMetadata,
    editorMetadata: {
      assetUrl: props?.requestProps?.assetUrl,
      isPlugin: props?.requestProps?.isPlugin,
      apiKey: props?.requestProps?.apiKey,
      branch: props?.requestProps?.branch,
      isFullScreen
    },
    stackMetadata,
    fieldMetadata: pick(fieldMetadata, fieldMetadataKeys),
    docUID: slateValue[0].uid,
    setDiscussions,
    discussions
  }
  const handleFocus = (e) => {
    onFocus(e)
  }
  const handleEmptyClick = (event) => {
    if (event.clientX > event.target.clientWidth || event.clientY > event.target.clientHeight) return
    const endPoint = Editor.end(editor, [])
    const { path } = endPoint
    const node = Node.get(editor, path.slice(0, 2))
    ReactEditor.focus(editor)
    if (Editor.isVoid(editor, node)) {
      Transforms.select(editor, path)
    } else {
      Transforms.select(editor, endPoint)
    }
  }

  const handleFocusForAccessibility = (e) => {
    const toolbarParent = e?.target?.getElementsByClassName('toolbar-parent')
    if (toolbarParent?.length && toolbarParent[0]?.childNodes?.length) {
      Array.from(toolbarParent[0]?.childNodes).forEach((child: any, index: any) => {
        if (index === 0) return
        let i = 0
        while (i < child.childNodes?.length) {
          if (!child.childNodes[i].className.includes('divider')) {
            child.childNodes[i]?.setAttribute("tabIndex", "-1")
          }
          i++
        }
      })
    }
    const editableElement = e?.target?.querySelector('#scrte-editable')
    if (editableElement) editableElement.setAttribute('tabIndex', "-1")
  }

  const handleKeyDownForAccessibility = (e) => {
    if (e.key === "Enter") {
      e?.target?.querySelector('span[data-icon="bold"]')?.focus()
      const toolbarParent = e?.target?.getElementsByClassName('toolbar-parent')
      if (toolbarParent?.length && toolbarParent[0]?.childNodes?.length) {
        Array.from(toolbarParent[0]?.childNodes).forEach((child: any, index: any) => {
          if (index === 0) return
          let i = 0
          while (i < child.childNodes?.length) {
            if (!child.childNodes[i].className.includes('divider')) {
              child.childNodes[i]?.setAttribute("tabIndex", "0")
            }
            i++
          }
        })
      }
      const editableElement = e?.target?.querySelector('#scrte-editable')
      if (editableElement) editableElement.setAttribute('tabIndex', "0")
    }
    if (isHotkey('shift+meta+enter', e)) {
      e.preventDefault()
      handleEmptyClick(e)
    }
    if (e.key === 'Escape') {
      e.target?.closest('#scrte-editor')?.focus()
    }
  }

  useEffect(() => {
    if (!editor.savedSelection) {
      savedSelection(editor)
    }
  }, [])

  const handleBlur = (e) => {
    onBlur(e)
    savedSelection(editor)
  }

  useEffect(() => {
    editor.uniqueId = v4().split('-').join('')
    editor.allowExtraTags = allowExtraTags
    editor.contentTypeNameMap = contentTypeNameMap
    editor.customPasteOptions = customPasteOptions || {}
    editor.copyPasteConfig = copyPasteConfig
    editor.csOnlyBreakline = csOnlyBreakline
    editor.customLocaleRTL = customLocaleRTL
    editor.csBreakLineOnEnter = csBreakLineOnEnter;
    editor.allLocales = allLocales
    editor.rteVersion = rteVersion
    editor.referenceActions = referenceActions;
  }, [copyPasteConfig, allLocales, rteVersion])
  useEffect(() => {
    const docUID = slateValue?.[0]?.uid
    // If comments is enabled then fetch all discussion for this entry
    if (comments.enabled) {
      setIsFetchingDiscussion(true)
      fetchActiveDiscussions(
        requestProps,
        docUID,
        entryMetadata.contentTypeUid,
        entryMetadata.entryUid,
        entryMetadata.locale
      )
        .then((res) => {
          const discussionMap = {}
          Array.from(res.discussions).forEach((discussion) => {
            if (discussion.field.block_uid) {
              discussionMap[discussion.field.block_uid] = discussion
            } else {
              discussionMap[`range-${discussion.uid}`] = discussion
            }
          })
          setDiscussions(discussionMap)
          if (!res.isCachedValue)
            setIsFetchingDiscussion(false)
        })
        .catch((error) => {
          setIsFetchingDiscussion(false)
          failureNotification(
            error?.data?.error_message || 'Error while fetching active discussions.',
            error?.data?.errors
          )
        })
    }
  }, [plugins.length, props.discussionRandomUid])

  useExtensionFullScreen(editor?.requestProps?.isPlugin, editorRef)
  useEditorResize(editorResizeRef, false)
  useRtePlugins({ plugins: props.plugins, setPlugins, editor, editorRef })

  const decorate = useDecorate(editor, [plugins.length])
  const {
    onChangeSlash,
    onKeyDownSlash,
    target,
    search,
    index,
    choice,
    setChoice,
    setTarget,
    searchElements,
    handleSet
  } = SlashElement(editor)
  const handleKeyDownForSlash = useCallback(
    (event) => {
      onKeyDownSlash({ event, editor })
    },
    [target, search, index]
  )

  const hoveringContext: IHoveringToolbarContext = useMemo(() => {
    return {
      discussions: discussions,
      setActiveDiscussion: setActiveDiscussion,
      commentsEnabled: comments?.enabled
    }
  }, [activeDiscussion, discussions, comments?.enabled])

  const minHeight = fieldMetadata?.field_metadata?.min_height
  const maxHeight = fieldMetadata?.field_metadata?.max_height
  let customHeightStyle = {}
  // apply custom styles only on non-fullscreen mode
  if (!isFullScreen) {
    // 300px is set as min-height through css so we need to override it here
    if (!minHeight && maxHeight && +maxHeight < 300) {
      customHeightStyle = {
        minHeight: maxHeight + 'px',
        maxHeight: maxHeight && maxHeight + 'px',
      }
    } else {
      customHeightStyle = {
        minHeight: minHeight && minHeight + 'px',
        maxHeight: maxHeight && maxHeight + 'px',
      }
    }
  }

  const editorOnChange = (newValue) => {
    if (!Array.isArray(editor.operations) || !editor.operations.every(Operation.isSelectionOperation)) {
      setSlateValue(newValue);
      onChange(newValue);
    }
    onChangeSlash(scrollRef)
  }

  const scrollSelectionIntoView = useCallback((editor, domRange) => {
    // Only scroll if the selection is not already visible in the viewport
    if (domRange) {
      const rect = domRange.getBoundingClientRect()
      const scroller = scrollRef.current
      
      if (scroller) {
        const scrollerRect = scroller.getBoundingClientRect()
        const isVisible = 
          rect.top >= scrollerRect.top &&
          rect.bottom <= scrollerRect.bottom &&
          rect.left >= scrollerRect.left &&
          rect.right <= scrollerRect.right

        if (!isVisible) {
          const scrollTop = scroller.scrollTop
          const scrollHeight = scroller.scrollHeight
          const clientHeight = scroller.clientHeight
          const rangeTop = rect.top - scrollerRect.top + scrollTop
          
          if (rangeTop < scrollTop) {
            scroller.scrollTo({ top: rangeTop - 20, behavior: 'smooth' })
          } else if (rangeTop + rect.height > scrollTop + clientHeight) {
            scroller.scrollTo({ top: rangeTop + rect.height - clientHeight + 20, behavior: 'smooth' })
          }
        }
      }
    }
  }, [])

  return (
    <ReduxProvider>
      <EditorProvider.Provider value={newProps}>
        <ScrollRefProvider.Provider value={{ scrollRef }}>
          <Slate
            editor={editor}
            value={slateValue}
            onChange={editorOnChange}>
            <div ref={singleLineRef} contentEditable={false} className={cx(dndStyles['scrte-dragline'])} />
            <div
              ref={editorRef}
              tabIndex={0}
              onFocus={handleFocusForAccessibility}
              onKeyDown={handleKeyDownForAccessibility}
              id="scrte-editor"
              className={cx(styles['super-charged-rte'], 'super-charged-rte active', disabled && styles['disabled'])}>
              {toolbar && (
                <>
                  {!isFullScreen ? (
                    <div className={cx(styles['scrte-fullscreen-row'])}  >
                      <FullScreenToggle editorRef={editorRef} editor={editor} isFullScreen={isFullScreen} isEntryInModal={props.isEntryInModal} />
                    </div>
                  ) : null}

                  <MainToolbar
                    ref={toolbarSizeRef}
                    customToolbarComponents={customToolbarComponentsCopy}
                    className={cx('scrte-toolbar', styles['scrte-main-toolbar'])}
                    contentStack={contentStack}
                    toolbarMode={toolbarMode}
                    fieldName={fieldName}
                    editorRef={editorRef}
                    pluginslen={plugins.length}
                    FullScreenToggle={<FullScreenToggle editorRef={editorRef} editor={editor} isFullScreen={isFullScreen} isEntryInModal={props.isEntryInModal} />}
                  />
                </>
              )}
              {!disabled && hoveringToolbar && (
                <HoveringToolbarProvider.Provider value={hoveringContext}>
                  <HoveringToolbar
                    customToolbarComponents={customToolbarComponentsCopy}
                    scrollRef={scrollRef}
                    contentStack={contentStack}
                    toolbarMode={toolbarMode}
                    pluginslen={plugins.length}
                  />
                </HoveringToolbarProvider.Provider>
              )}
              <div
                id="scroller"
                className={cx('scrte-scroller', `scrte-editor-${editor.uniqueId}`)}
                ref={scrollRef}
                style={customHeightStyle}
              >
                <>
                  <Editable
                    {...dndResetProps}
                    onMouseDown={handleEmptyClick}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    renderElement={elementRenderer}
                    renderLeaf={leafRenderer}
                    onKeyDown={handleKeyDown}
                    scrollSelectionIntoView={scrollSelectionIntoView}
                    autoCorrect="true"
                    spellCheck="true"
                    id="scrte-editable"
                    readOnly={disabled}
                    tabIndex={-1}
                    //@ts-ignore
                    className={cx(
                      siteViewStyles['editable'],
                      props.className,
                      styles['scrte-editable'],
                      'scrte-editable'
                    )}
                    required={required}
                    decorate={decorate}
                    style={customHeightStyle}
                  />

                  {!editor?.requestProps?.isPlugin && <div className="editor-resize" ref={editorResizeRef}></div>}
                  <ResizeIcon />
                </>
              </div>
            </div>
            <div
              className="rte-fullscreen-overlay"
              onClick={() => handleRteOverlayClick(editorRef, editor.requestProps.isPlugin, props.isEntryInModal)}></div>
            {!disabled && <SlashComponent
              index={index}
              target={target}
              searchElements={searchElements}
              editor={editor}
              choice={choice}
              setChoice={setChoice}
              handleSet={handleSet}
              setTarget={setTarget}
              scrollRef={scrollRef}
            />}
            <DiscussionPopup
              activeDiscussion={activeDiscussion}
              setActiveDiscussion={setActiveDiscussion}
              docUID={slateValue[0].uid}
              scrollRef={scrollRef}
              removeDiscussion={removeDiscussion}
              editorRef={editorRef}
              {...discussionProps}
            />
          </Slate>
        </ScrollRefProvider.Provider>
      </EditorProvider.Provider>
    </ReduxProvider>
  )
}


export default EditorElement
